/**
 * Which editor tabs the signed-in user is allowed to see.
 *
 * A superAdmin can narrow a user to a subset of the version editor's panels,
 * separately for the two tab sets — a legacy playable's editor and a
 * playEngine's editor have almost nothing in common. The lists arrive with the
 * session (validateAccessToken) and are cached in localStorage by home.js, the
 * same way permissions and the company are.
 *
 * The rule is: **no list means every tab except the opt-in ones**. Reading a
 * missing key as an empty list would leave the user with an editor of no panels,
 * so every read goes through here.
 *
 * pe3d bases have a third set (Config, Playable, Assets, Release). Their Agent
 * tab is not in it: it follows the playEngine set's "agent".
 *
 * Agent is the opt-in one — it spends money and edits a version by itself, so it
 * is ticked for the people who should have it rather than handed to everybody.
 * Mirrors OPT_IN_TABS in controller/user/editorTabs.js. It follows the person,
 * not the company: a user keeps its Agent tab after switching into a company
 * that has no agent of its own, and the credits come from its own company.
 *
 * This only hides panels — the version's data is already in the browser. It
 * keeps somebody out of a tab that is not their job, it does not withhold
 * anything. Real restrictions belong in permissions.
 */

const OPT_IN_TABS = ["agent"];

const STORAGE_KEY = {
  legacy: "user.legacyTabs",
  playEngine: "user.playEngineTabs",
  pe3d: "user.pe3dTabs",
};

/** Stores the session's answer; `null` means unrestricted. */
export function cacheTabRestrictions(session) {
  const user = (session && session.user) || {};
  for (const setName of Object.keys(STORAGE_KEY)) {
    const list = user[`${setName}Tabs`];
    localStorage.setItem(STORAGE_KEY[setName], Array.isArray(list) ? JSON.stringify(list) : "");
  }
}

/** The allowed keys, or null when this user sees everything. */
export function getTabRestriction(setName) {
  const raw = localStorage.getItem(STORAGE_KEY[setName]);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : null;
  } catch (err) {
    // A half-written value is not worth locking the editor over.
    return null;
  }
}

/**
 * Keep only the tabs this user may see, out of the ones the editor was going to
 * render.
 *
 * For Agent this list is the whole decision — the editor offers the tab to
 * everyone and this is what takes it away. A run is still refused when the
 * user's own company is out of AI credits, but that is money, not visibility.
 */
export function filterTabDefs(defs, setName) {
  const byDefault = defs.filter((tab) => OPT_IN_TABS.indexOf(tab.key) === -1);
  const allowed = getTabRestriction(setName);
  if (!allowed) {
    return byDefault;
  }
  const permitted = new Set(allowed);
  const kept = defs.filter((tab) => permitted.has(tab.key));
  // An empty result would render an editor with no panels at all. The screen
  // that sets this refuses to save an empty list, but a tab renamed in a later
  // release could still empty it out, so fall back to the default set.
  return kept.length > 0 ? kept : byDefault;
}
